"use client";

/**
 * GetSkillDisplay — tool display for get_skill(skill_id).
 *
 * Shown when the agent proactively activates a prebuilt skill (e.g. Ticker
 * Deep Dive) instead of waiting for the user to click it. Matches the compact
 * in-stream card shape used by SwitchModeDisplay: rounded-md, px-3 py-2,
 * inline w-3.5 h-3.5 icon, text-[13px] font-medium.
 *
 * The big engineered instruction never reaches the browser (stripped by the
 * backend stream filter); this card only reads the small chip fields.
 */

import { motion } from "framer-motion";
import { Sparkles, Loader2 } from "lucide-react";

interface GetSkillDisplayProps {
  status: "loading" | "success";
  result?: {
    status?: string;
    skill_used?: boolean;
    skill_used_name?: string;
    skill_used_id?: string;
    noop?: boolean;
    message?: string;
  };
  args?: {
    skill_id?: string;
  };
}

export function GetSkillDisplay({ status, result, args }: GetSkillDisplayProps) {
  const name =
    (result?.skill_used_name || "").trim() ||
    (args?.skill_id || "").trim() ||
    "skill";

  const Icon = status === "loading" ? Loader2 : Sparkles;
  const spin = status === "loading";

  return (
    <motion.div
      initial={{ opacity: 0, y: 4, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      className="w-full"
    >
      <div className="rounded-md border overflow-hidden border-violet-300/25 bg-violet-300/[0.03]">
        <div className="px-3 py-2 flex items-center gap-2.5 min-w-0">
          <Icon
            className={`w-3.5 h-3.5 shrink-0 text-violet-100 ${spin ? "animate-spin" : ""}`}
            strokeWidth={2}
          />
          <span className="text-[13px] font-medium truncate text-violet-50">
            {status === "loading" ? "Selecting skill" : `Using: ${name}`}
          </span>

          <span className="flex-1" />

          {status === "success" && (
            <span className="shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider tabular-nums border-violet-300/35 bg-violet-300/[0.08] text-violet-50">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse bg-violet-200" />
              SKILL
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
}
